import { TicketHandler } from '@/modules/Ticket/types/response'
import { Crown, Medal, Trophy, UserX } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

interface TopTicketHandlerProps {
    top_ticket_handlers: TicketHandler[]
}

const getRankIcon = (idx: number) => {
    switch (idx) {
        case 0:
            return <Crown className="w-3.5 h-3.5 text-yellow-500" />
        case 1:
            return <Medal className="w-3.5 h-3.5 text-gray-400" />
        case 2:
            return <Trophy className="w-3.5 h-3.5 text-amber-600" />
        default:
            return null
    }
}

const getResolutionRate = (handler: TicketHandler) => {
    if (handler.total_tickets === 0) return 0
    return Math.round((handler.resolved_tickets / handler.total_tickets) * 100)
}

const TopTicketHanler: React.FC<TopTicketHandlerProps> = ({
    top_ticket_handlers,
}) => {
    const { t } = useTranslation()

    // Separate unassigned (employee_id = 0) from assigned handlers
    const { assignedHandlers, unassignedTickets } = useMemo(() => {
        const unassigned = top_ticket_handlers.find((h) => h.employee_id === 0)
        const assigned = top_ticket_handlers.filter((h) => h.employee_id !== 0)
        return { assignedHandlers: assigned, unassignedTickets: unassigned }
    }, [top_ticket_handlers])

    return (
        <div className="bg-white rounded-[8px] p-4 lg:col-span-3 col-span-12 half-col-print">
            <Typogaphy
                size="small"
                className="text-primary font-semibold capitalize"
            >
                {t('top-ticket-handlers')} (
                {top_ticket_handlers?.reduce(
                    (acc, item) => acc + item.total_tickets,
                    0
                )}
                )
            </Typogaphy>

            {top_ticket_handlers.length === 0 ? (
                <div className="min-h-[280px] flex items-center justify-center">
                    <Typogaphy className="text-gray-500 text-center text-sm">
                        {t('no-data')}
                    </Typogaphy>
                </div>
            ) : (
                <div className="mt-3 border rounded-sm overflow-hidden">
                    {/* Unassigned tickets row */}
                    {unassignedTickets &&
                        unassignedTickets.total_tickets > 0 && (
                            <div className="flex items-center px-3 py-2 border-b bg-orange-50/50">
                                <div className="w-6 flex justify-center">
                                    <UserX className="w-3.5 h-3.5 text-orange-500" />
                                </div>
                                <div className="flex-1 min-w-0 ml-2">
                                    <span className="text-sm text-orange-600 font-medium">
                                        {t('unassigned')}
                                    </span>
                                    <span className="text-xs text-orange-400 ml-2">
                                        {t('ticket-needs-assignment')}
                                    </span>
                                </div>
                                <div className="text-sm font-semibold text-orange-600">
                                    {unassignedTickets.total_tickets}
                                </div>
                            </div>
                        )}

                    {/* Assigned handlers rows */}
                    {assignedHandlers?.slice(0, 5).map((handler, idx) => {
                        const rate = getResolutionRate(handler)
                        const isLast =
                            idx === Math.min(assignedHandlers.length - 1, 4)
                        return (
                            <div
                                key={handler.employee_id}
                                className={`flex items-center px-3 py-2 hover:bg-gray-50 ${!isLast ? 'border-b' : ''}`}
                            >
                                {/* Rank */}
                                <div className="w-6 flex justify-center text-xs text-gray-400 font-medium">
                                    {getRankIcon(idx) || idx + 1}
                                </div>

                                {/* Name & Stats */}
                                <div className="flex-1 min-w-0 ml-2">
                                    <span
                                        className="text-sm text-gray-800 truncate block"
                                        title={handler.employee_name}
                                    >
                                        {handler.employee_name}
                                    </span>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <span>
                                            {handler.resolved_tickets}/
                                            {handler.total_tickets}
                                        </span>
                                        <span
                                            className={
                                                rate >= 80
                                                    ? 'text-green-600'
                                                    : rate >= 50
                                                      ? 'text-yellow-600'
                                                      : 'text-red-500'
                                            }
                                        >
                                            {rate}%
                                        </span>
                                    </div>
                                </div>

                                {/* Total */}
                                <div className="text-sm font-semibold text-primary">
                                    {handler.total_tickets}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default TopTicketHanler
